import { Divider, Typography } from "antd"
import { CheckCircleOutlined } from "@ant-design/icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrafficCone, faBan, faCalendarDay, faTriangleExclamation, faSnowflake } from "@fortawesome/pro-solid-svg-icons"
import dayjs from "dayjs"
import { DateRangeViewEvent, Timesheet, VisitWorkareaStatusRecord } from "./scheduleApi"
import { EventWarning, getConfiguredWorkAreas } from "./scheduleModel"
import { buildStatusItems, formatAddress, formatWorkAreas, formatEstimatedHours, shouldShowStatusSection } from "./eventPopoverModel"
import { VisitWorkareaStatuses } from "../../common/constants"

const { Text } = Typography

type Props = {
  event: DateRangeViewEvent
  workareaStatuses?: VisitWorkareaStatusRecord[]
  timesheets?: Timesheet[]
  warning?: EventWarning
  rescheduledToDate?: string | null
}

const getStatusDisplay = (status: number) => {
  switch (status) {
    case VisitWorkareaStatuses.InProgress:
      return {
        icon: <FontAwesomeIcon icon={faTrafficCone} style={{ color: '#fa8c16', fontSize: 12 }} />,
        label: 'In Progress',
        color: '#fa8c16',
      }
    case VisitWorkareaStatuses.Completed:
      return {
        icon: <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />,
        label: 'Completed',
        color: '#52c41a',
      }
    case VisitWorkareaStatuses.CompletedUnbillable:
      return {
        icon: <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />,
        label: 'Completed (Unbillable)',
        color: '#52c41a',
      }
    case VisitWorkareaStatuses.Skipped:
      return {
        icon: <FontAwesomeIcon icon={faBan} style={{ color: '#000', fontSize: 12 }} />,
        label: 'Skipped',
        color: '#000',
      }
    case VisitWorkareaStatuses.SkippedReschedule:
      return {
        icon: <FontAwesomeIcon icon={faCalendarDay} style={{ color: '#000', fontSize: 12 }} />,
        label: 'Skipped (Reschedule)',
        color: '#000',
      }
    default:
      return null
  }
}

const EventPopoverContent = ({ event, workareaStatuses, timesheets, warning, rescheduledToDate }: Props) => {
  const isRescheduled = event.rescheduledToEventId != null
  const rescheduledMessage = isRescheduled
    ? (rescheduledToDate ? `Rescheduled to ${dayjs(rescheduledToDate).format('MMM D, YYYY')}` : 'Rescheduled')
    : null
  const addressLines = formatAddress(event.property)
  const configuredWorkAreas = getConfiguredWorkAreas(event)
  const workAreasDisplay = formatWorkAreas(configuredWorkAreas)
  const estimatedHoursDisplay = formatEstimatedHours(configuredWorkAreas)
  const hasCustomerInfo = event.customer?.displayName || event.property?.nickname || (addressLines && addressLines.length > 0)
  const statusItems = buildStatusItems(configuredWorkAreas, event.workAreas ?? [], workareaStatuses, timesheets)
  const showStatusSection = shouldShowStatusSection(statusItems)

  return (
    <div style={{ maxWidth: 280 }}>
      {event.frozen && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', marginBottom: 8, backgroundColor: '#e6f4ff', border: '1px solid #91caff', borderRadius: 4 }}>
          <FontAwesomeIcon icon={faSnowflake} style={{ color: '#0958d9', fontSize: 12, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#0958d9' }}>Frozen — cannot be rescheduled</span>
        </div>
      )}
      {rescheduledMessage && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', marginBottom: 8, backgroundColor: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 4 }}>
          <FontAwesomeIcon icon={faCalendarDay} style={{ color: '#2f54eb', fontSize: 12, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#1d39c4' }}>{rescheduledMessage}</span>
        </div>
      )}
      {warning && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', marginBottom: 8, backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4 }}>
          <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#fa8c16', fontSize: 12, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#ad6800' }}>{warning.message}</span>
        </div>
      )}
      <div style={{ fontWeight: 600 }}>
        {event.jobNumber && <Text type="secondary" style={{ marginRight: 8, fontSize: 'inherit' }}>{event.jobNumber}</Text>}
        {event.jobName}
      </div>
      {workAreasDisplay && (
        <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>{workAreasDisplay}</div>
      )}
      {estimatedHoursDisplay && (
        <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>{estimatedHoursDisplay}</div>
      )}
      {showStatusSection && (
        <>
          <Divider style={{ margin: "12px 0" }} />
          {statusItems.map((item, index) => {
            const display = item.status !== null ? getStatusDisplay(item.status) : null
            return (
              <div key={`${item.workareaId}-${item.status ?? 'none'}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 13 }}>
                {display ? (
                  <>
                    {display.icon}
                    <span>{item.name} — {display.label}</span>
                  </>
                ) : item.hasTimesheets ? (
                  <>
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                    <span>{item.name}{item.manhoursDisplay && ` — ${item.manhoursDisplay}`}</span>
                  </>
                ) : (
                  <span style={{ color: '#999' }}>{item.name} — No Time Recorded</span>
                )}
              </div>
            )
          })}
        </>
      )}
      {hasCustomerInfo && (
        <>
          <Divider style={{ margin: "12px 0" }} />
          {event.customer?.displayName && (
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{event.customer.displayName}</div>
          )}
          {event.property?.nickname && event.property.nickname !== event.customer?.displayName && (
            <div style={{ color: "#666", marginBottom: 4 }}>{event.property.nickname}</div>
          )}
          {addressLines && addressLines.length > 0 && (
            <div style={{ color: "#666", fontSize: 13 }}>
              {addressLines.map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default EventPopoverContent
